<template>
  <AppDialog
    v-model="isOpen"
    is-modal
    no-auto-close
    class="tw-w-[600px] tw-overflow-visible"
    overflow="visible"
    title="Audiodatei aus Mitschnittservice importieren"
  >
    <div class="form-group">
      <label id="cutter-start-time-label" class="tw-font-bold">Startzeit</label>

      <AppDateInput v-model="startTime" seek disabled />

      <AppRecordingTimeInput
        id="cutter-start-time"
        v-model="startTime"
        aria-labelledby="cutter-start-time-label"
        :events="startEvents"
        class="tw-z-10 tw-mt-2"
      />

      <AppPrelistenPlaybutton
        v-model="startTime"
        :api-base="props.apiBase"
        :start-offset="0"
        :end-offset="10"
        class="tw-z-10"
      />
    </div>

    <div class="form-group">
      <label id="cutter-end-time-label" class="tw-font-bold">Endzeit</label>

      <AppDateInput v-model="endTime" seek disabled />

      <AppRecordingTimeInput
        id="cutter-end-time"
        v-model="endTime"
        aria-labelledby="cutter-end-time-label"
        :events="endEvents"
        class="tw-z-10 tw-mt-2"
      />
    </div>

    <AppPrelistenPlaybutton
      v-model="startTime"
      :api-base="props.apiBase"
      :start-offset="-5"
      :end-offset="0"
      class="tw-z-10 tw-mb-3"
    />

    <p v-if="error" class="tw-p-3 tw-rounded tw-bg-rose-600 tw-text-white">
      {{ error }}
    </p>

    <button type="button" class="btn btn-primary" @click="submitRecordingRequest">
      Audiodatei importieren
    </button>
  </AppDialog>
</template>

<script lang="ts" setup>
import { watchDebounced } from '@vueuse/core'
import { ref } from 'vue'
import { RecordingEvent } from '../types'
import AppDateInput from './AppDateInput.vue'
import AppDialog from './AppDialog.vue'
import AppPrelistenPlaybutton from './AppPrelistenPlaybutton.vue'
import AppRecordingTimeInput from './AppRecordingTimeInput.vue'
import { createURLBuilder, getCookie } from './util'

const props = defineProps<{
  apiBase: string
  reportId: number
  start: string
  end: string
}>()

const reportId = ref(props.reportId)
const startTime = ref(props.start)
const endTime = ref(props.end)
const isOpen = ref(false)
const error = ref('')

const startEvents = ref<RecordingEvent[]>([])
const endEvents = ref<RecordingEvent[]>([])

const buildURL = createURLBuilder(props.apiBase, true)

watchDebounced(
  startTime,
  async (time) => {
    startEvents.value = await fetchEvents(time)
  },
  { immediate: true, debounce: 500 },
)

watchDebounced(
  endTime,
  async (time) => {
    endEvents.value = await fetchEvents(time)
  },
  { immediate: true, debounce: 500 },
)

async function fetchEvents(surrounding: string): Promise<RecordingEvent[]> {
  // The backend doesn’t like the Z as the UTC zone designator, so we replace it with +00:00
  const query = new URLSearchParams({ surrounding: surrounding.replace(/Z$/, '+00:00') })
  const res = await fetch(buildURL('events', query))
  if (res.ok) {
    return (await res.json()).results as RecordingEvent[]
  } else {
    console.error('Could not fetch events from API', res)
    return []
  }
}

async function submitRecordingRequest() {
  error.value = ''

  const headers = new Headers()
  const csrf = getCookie('csrftoken')
  if (csrf) headers.set('x-csrftoken', csrf)
  headers.set('Content-Type', 'application/json')

  const res = await fetch(buildURL('recordings'), {
    method: 'post',
    headers,
    body: JSON.stringify({
      start_time: startTime.value,
      end_time: endTime.value,
      report: reportId.value,
    }),
  })

  if (res.ok) {
    isOpen.value = false
  } else {
    console.error('Could not submit recording request', res, await res.text())
    error.value = res.statusText
  }
}

function openDialog() {
  isOpen.value = true
}

defineExpose({
  openDialog,
})
</script>
